import React from 'react';

import {
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {HelpOutline as HelpOutlineIcon} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {defineMessages, useIntl} from 'react-intl';
import {useMutation, useQueryClient} from 'react-query';

import API, {BlobData, useAuth, useCsrfToken} from '@wheeebo/polaris-api';
import {useSnackbar} from '@wheeebo/polaris-snackbar';
import {EditableField, EditableImageField, ValidatableField, useErrorHandler} from '@wheeebo/polaris-ui';

const msgs = defineMessages({
  headerTitle: {
    id: 'pages.member.settings.AccountCard.header.title',
    defaultMessage: 'Settings',
  },
  inputAvatarTitle: {
    id: 'pages.member.settings.AccountCard.input.avatarTitle',
    defaultMessage: 'Avatar Image',
  },
  inputAvatarHelperText1: {
    id: 'pages.member.settings.AccountCard.input.avatarHelperText1',
    defaultMessage: 'The file size must be less than 10 MB.',
  },
  inputAvatarHelperText2: {
    id: 'pages.member.settings.AccountCard.input.avatarHelperText2',
    defaultMessage: 'Your profile picture will be displayed in a circle with an aspect ratio of 1:1.',
  },
  inputMemberIdTitle: {
    id: 'pages.member.settings.AccountCard.input.memberIdTitle',
    defaultMessage: 'Member ID',
  },
  inputMemberIdHelperText1: {
    id: 'pages.member.settings.AccountCard.input.memberIdHelperText1',
    defaultMessage: 'This is your member ID, which cannot be changed.',
  },
  inputDisplayNameTitle: {
    id: 'pages.member.settings.AccountCard.input.displayNameTitle',
    defaultMessage: 'Name',
  },
  inputDisplayNamePlaceholder: {
    id: 'pages.member.settings.AccountCard.input.displayNamePlaceholder',
    defaultMessage: 'Enter your name',
  },
  inputEmailTitle: {
    id: 'pages.member.settings.AccountCard.input.emailTitle',
    defaultMessage: 'Email',
  },
  inputPasswordTitle: {
    id: 'pages.member.settings.AccountCard.input.passwordTitle',
    defaultMessage: 'Password',
  },
  successAvatarUpdated: {
    id: 'pages.member.settings.AccountCard.success.avatarUpdated',
    defaultMessage: 'Avatar updated',
  },
  successDisplayNameUpdated: {
    id: 'pages.member.settings.AccountCard.success.DisplayNameUpdated',
    defaultMessage: 'Display name updated',
  },
});

const AccountUpdateCard = () => {
  const intl = useIntl();
  const {csrfToken} = useCsrfToken();
  const {showAndEmitJsErrorEvent} = useErrorHandler();
  const {showSuccess} = useSnackbar();
  const queryClient = useQueryClient();
  const {register, formState} = useForm({mode: 'onChange'});
  const {errors} = formState;
  const {account} = useAuth();
  const navigate = useNavigate();
  const [name, setName] = React.useState(account?.name || '');
  const {mutate: changeDisplayName} = useMutation<unknown, unknown, string>(
    (name: string) => API.Accounts.update(csrfToken, {name}),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(API.Accounts.CacheKey);
        showSuccess(intl.formatMessage(msgs.successDisplayNameUpdated));
        return;
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  const {mutate: updateAccount} = useMutation<unknown, unknown, BlobData | undefined>(
    (blobData?: BlobData) => {
      if (blobData) {
        return API.Accounts.update(csrfToken, {avatar: blobData});
      }
      return API.Accounts.update(csrfToken, {deleteAvatar: true});
    },
    {
      onSuccess: (_, blobData?: BlobData) => {
        queryClient.invalidateQueries(API.Accounts.CacheKey);
        if (blobData) {
          showSuccess(intl.formatMessage(msgs.successAvatarUpdated));
          return;
        }
        showSuccess(intl.formatMessage(msgs.successAvatarUpdated));
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  const handleDisplayNameChange = (name: string) => {
    if (!errors['name']) {
      changeDisplayName(name);
      return true;
    }
    return false;
  };
  return (
    <Card>
      <CardHeader />
      <CardContent>
        <Grid container flexDirection="row" columnSpacing={1}>
          <Grid item xs={12} sm={4}>
            <FormControl component="fieldset" fullWidth>
              <EditableImageField
                editable
                locked
                url={account?.avatarUrl || undefined}
                onSave={updateAccount}
                maxWidth={250}
                maxHeight={250}
                maxFileSize={'10MB'}
              />
              <Stack direction="row" sx={{justifyContent: 'space-around', alignItems: 'center'}}>
                <FormLabel component="legend">{intl.formatMessage(msgs.inputAvatarTitle)}</FormLabel>
                <Tooltip
                  enterTouchDelay={0}
                  title={
                    <Stack>
                      <Typography variant="caption">{intl.formatMessage(msgs.inputAvatarHelperText1)}</Typography>
                      <Typography variant="caption">{intl.formatMessage(msgs.inputAvatarHelperText2)}</Typography>
                    </Stack>
                  }
                  arrow>
                  <HelpOutlineIcon fontSize="small" color="action" />
                </Tooltip>
              </Stack>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Stack gap={2}>
              <FormControl component="fieldset" fullWidth>
                <EditableField label={intl.formatMessage(msgs.inputMemberIdTitle)} value={account?.id || ''} locked />
                <FormHelperText sx={{mx: 0}}>{intl.formatMessage(msgs.inputMemberIdHelperText1)}</FormHelperText>
              </FormControl>
              <FormControl component="fieldset" fullWidth>
                <ValidatableField
                  id="displayName"
                  label={intl.formatMessage(msgs.inputDisplayNameTitle)}
                  placeholder={intl.formatMessage(msgs.inputDisplayNamePlaceholder)}
                  fullWidth
                  value={name}
                  onSave={handleDisplayNameChange}
                  editable
                  locked
                  {...{errors, register}}
                  registerOptions={{
                    value: name,
                    required: true,
                    maxLength: 128,
                    onChange: (e) => setName(e.target.value),
                  }}
                />
              </FormControl>
              <FormControl component="fieldset" fullWidth>
                <ValidatableField
                  id="email"
                  label={intl.formatMessage(msgs.inputEmailTitle)}
                  fullWidth
                  onEdit={() => navigate('/settings/update_email')}
                  editable
                  locked
                  {...{errors, register}}
                  registerOptions={{
                    value: account?.email || '',
                    required: true,
                    maxLength: 128,
                    onChange: undefined,
                  }}
                />
              </FormControl>
              <FormControl component="fieldset" fullWidth>
                <ValidatableField
                  id="password"
                  autoComplete="off"
                  label={intl.formatMessage(msgs.inputPasswordTitle)}
                  fullWidth
                  onEdit={() => navigate('/settings/update_password')}
                  editable
                  locked
                  type="password"
                  {...{errors, register}}
                  registerOptions={{
                    value: '*****',
                    required: true,
                    maxLength: 128,
                    onChange: undefined,
                  }}
                />
              </FormControl>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default React.memo(AccountUpdateCard);
